/**
 * Sidebar Component
 * Navigation sidebar with menu items and cycle progress
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  ClipboardCheck,
  Users,
  BarChart3,
  X,
  Network,
  Shield,
  Building2,
  CalendarCheck,
  TrendingUp
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { useSidebarProgress, getCurrentQuarter } from '../../hooks/useSidebarProgress';

export interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  allowedRoles?: string[];
  badge?: number;
}

// Tenant-specific navigation (NOT for super_admin)
const tenantNavigation: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    allowedRoles: ['account_admin', 'manager', 'employee', 'viewer'],
  },
  {
    name: 'My OKRs',
    href: '/okrs',
    icon: Target,
    allowedRoles: ['account_admin', 'manager', 'employee', 'viewer'],
  },
  {
    name: 'Alignment Map',
    href: '/okrs/alignment',
    icon: Network,
    allowedRoles: ['account_admin', 'manager', 'employee', 'viewer'],
  },
  {
    name: 'Check-ins',
    href: '/check-ins',
    icon: CalendarCheck,
    allowedRoles: ['account_admin', 'manager', 'employee', 'viewer'],
  },
  {
    name: 'Approvals',
    href: '/approvals/pending',
    icon: ClipboardCheck,
    allowedRoles: ['account_admin', 'manager'],
  },
  {
    name: 'Users',
    href: '/users',
    icon: Users,
    allowedRoles: ['account_admin', 'manager'],
  },
  {
    name: 'Performance',
    href: '/performance',
    icon: TrendingUp,
    allowedRoles: ['account_admin', 'manager', 'employee'],
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: BarChart3,
    allowedRoles: ['account_admin', 'manager'],
  },
  {
    name: 'Admin',
    href: '/admin',
    icon: Shield,
    allowedRoles: ['account_admin'],
  },
];

// Super admin only navigation
const superAdminNavigation: NavItem[] = [
  {
    name: 'Platform Admin',
    href: '/super-admin',
    icon: Building2,
    allowedRoles: ['super_admin'],
  },
];

// Combined navigation - super_admin gets their own nav, others get tenant nav
const getNavigation = (role: string | undefined): NavItem[] => {
  if (role === 'super_admin') {
    return superAdminNavigation;
  }
  return tenantNavigation;
};

// OKR Progress Ring Component
const CycleProgressRing: React.FC<{ progress: number; isLoading?: boolean; size?: number }> = ({
  progress,
  isLoading = false,
  size = 80
}) => {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="progress-ring"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          className="text-stone-200"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
        />
        <circle
          className={clsx(
            'transition-all duration-500',
            isLoading ? 'text-stone-300' : 'text-primary-500'
          )}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {isLoading ? (
          <div className="animate-pulse">
            <div className="h-5 w-10 bg-stone-200 rounded mb-1"></div>
            <div className="h-2 w-12 bg-stone-200 rounded"></div>
          </div>
        ) : (
          <>
            <span className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              {progress}%
            </span>
            <span className="text-[10px] uppercase tracking-wide text-stone-500">
              OKR Progress
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  const navigation = getNavigation(user?.role);
  const filteredNavigation = navigation.filter(
    (item) => !item.allowedRoles || (user?.role && item.allowedRoles.includes(user.role))
  );

  // Fetch real OKR progress data for current quarter (only for non-super_admin users)
  const { data: progressData, isLoading: isLoadingProgress } = useSidebarProgress(
    user?.role !== 'super_admin'
  );

  // Get current quarter info for display
  const currentCycle = getCurrentQuarter();

  // Use avgProgress from org-stats, fallback to 0 while loading
  const okrProgress = progressData?.avgProgress ?? 0;

  return (
    <>
      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-white border-r border-stone-200 transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header with Logo */}
          <div className="flex h-16 items-center justify-between px-5 border-b border-stone-100">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-600">
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>O</span>
              </div>
              <span className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                OKR Hub
              </span>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-600 lg:hidden"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* OKR Progress Widget */}
          {user?.role !== 'super_admin' && (
            <div className="mx-4 my-4 p-4 bg-sand rounded-xl text-center">
              <div className="flex justify-center mb-3">
                <CycleProgressRing progress={okrProgress} isLoading={isLoadingProgress} />
              </div>
              <div className="font-semibold text-stone-900">
                {currentCycle.name}
              </div>
              <div className="text-xs text-stone-500">
                {currentCycle.dateRange}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-2 overflow-y-auto">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 px-3 py-2">
              Main
            </div>
            {filteredNavigation.slice(0, 4).map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => onClose?.()}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-stone-600 hover:bg-sand hover:text-stone-900'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={clsx(
                        'h-5 w-5',
                        isActive ? 'text-primary-500' : 'text-stone-400'
                      )}
                    />
                    {item.name}
                    {item.badge && (
                      <span className="ml-auto bg-primary-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}

            {filteredNavigation.length > 4 && (
              <>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 px-3 py-2 mt-4">
                  Manage
                </div>
                {filteredNavigation.slice(4).map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => onClose?.()}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                        isActive
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-stone-600 hover:bg-sand hover:text-stone-900'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={clsx(
                            'h-5 w-5',
                            isActive ? 'text-primary-500' : 'text-stone-400'
                          )}
                        />
                        {item.name}
                      </>
                    )}
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          {/* User Card */}
          <div className="border-t border-stone-100 p-4">
            <NavLink
              to="/profile"
              onClick={() => onClose?.()}
              className="flex items-center gap-3 rounded-lg p-2 hover:bg-sand transition-colors"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-stone-300 to-stone-400 text-white text-sm font-semibold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-stone-900 truncate">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-stone-500 capitalize truncate">
                  {user?.role?.replace('_', ' ')}
                </div>
              </div>
            </NavLink>
          </div>
        </div>
      </aside>
    </>
  );
};
